import { AdvanceEvent, PingEvent, TickEvent } from "../events";
import { AttemptState, GlobalState } from "../model/datasource/state";

export interface Config {
	resolution: number;
	tickRate: number;
	pingRate: number;
	advanceRate: number;
	autoAdvance: boolean;
	recentCount: number;
	timed: boolean;
}

export type Settings = Pick<Config, "advanceRate" | "autoAdvance" | "timed">;

export interface TimeSpan<E extends TimerExtras> {
	start: number;
	end?: number;
	timed: boolean;
	extras?: E;
}

export interface TimerExtras { }

export abstract class Timer<E extends TimerExtras> {
	lastTick: number;
	lastPing: number;
	lastAdvance: number;
	initial: Record<number, number>;
	times: Record<number, TimeSpan<E>[]>;
	initial_times: Record<number, number>;
	config: Config;
	target: EventTarget;

	constructor() {
		this.lastTick = 0;
		this.lastPing = 0;
		this.lastAdvance = 0;
		this.initial = {};
		this.times = {};
	}

	init(bindTarget: EventTarget, config: Config, times: AttemptState["times"] | undefined, map: Record<string, number>): void {
		this.config = config;
		this.target = bindTarget;
		this.lastTick = new Date().getTime();
		this.initial = Object.entries(times ?? {}).reduce((result: Record<number, number>, [id, time]) => ({
			...result,
			[map[id]]: time
		}), {});
		setInterval(() => {
			const now = new Date().getTime();
			this.lastTick = this.check(now,
				this.lastTick, this.config.tickRate,
				() => { this.tick(); }
			);
		}, this.config.resolution);
	}

	tick(): void {
		const now = new Date().getTime();
		this.lastPing = this.check(now,
			this.lastPing, this.config.pingRate,
			() => {
				this.target.dispatchEvent(new PingEvent());
			}
		);
		if (this.config.autoAdvance) {
			this.lastAdvance = this.check(now,
				this.lastAdvance, this.config.advanceRate,
				() => {
					this.target.dispatchEvent(new AdvanceEvent());
				},
				this.config.resolution
			);
		}
		this.target.dispatchEvent(new TickEvent());
	}

	check(now: number, then: number, rate: number, event: () => void, epsilon: number = 0): number {
		if (now - then >= rate - epsilon) {
			event();
			return then + (rate * Math.trunc((now - then + epsilon) / rate));
		} else {
			return then;
		}
	}

	switch(previous: number, next: number, extras?: E): void {
		const now = new Date().getTime();
		if (
			previous in this.times
			&& this.times[previous].length > 0
			&& this.times[previous].at(-1)?.end === undefined
		) {
			const lastTime = this.times[previous].at(-1)!;
			lastTime.end = now;
		}
		if (!(next in this.times)) { this.times[next] = []; }
		this.times[next].push({ start: now, timed: this.config.timed, extras });
		this.lastAdvance = now;
		this.target.dispatchEvent(new PingEvent());
	}

	abstract viewContext(current: number, context: string): void

	abstract pause(current: number): void

	abstract unpause(current: number): void

	total(indices?: number[]): number {
		const hasKey = ([index, _]) => indices?.includes(parseInt(index)) ?? true;
		return Object.entries(this.times).filter(hasKey)
			.flatMap(([, times]) => times)
			.filter((time: TimeSpan<E>) => time.timed)
			.reduce((result: number, time: TimeSpan<E>) =>
				result + ((time.end ?? Date.now()) - time.start),
				Object.entries(this.initial).filter(hasKey)
					.reduce((result: number, [, time]) => result + time, 0)
			);
	}

	recent(): {index: number, time: TimeSpan<E>}[] {
		return Object.keys(this.times)
			.flatMap(index => this.times[index]
				.map((time: TimeSpan<E>) => ({
					index: parseInt(index),
					time: time
				}))
			)
			.sort((a, b) =>
				a.time.start - b.time.start
			)
			.slice(-this.config.recentCount);
	}

	toggleAutoAdvance(enable: boolean): void {
		const now = new Date().getTime();
		this.config.autoAdvance = enable;
		this.lastAdvance = now;
	}

	setAdvanceInterval(interval: number): void {
		const now = new Date().getTime();
		this.config.advanceRate = interval;
		this.lastAdvance = now;
	}

	abstract reset(current: number): void

	serializeTimes(map: string[]): AttemptState["times"] {
		return Object.keys(this.times)
			.concat(Object.keys(this.initial))
			.reduce((result, index) => ({
				...result,
				[map[index]]: this.total([parseInt(index)])
			}), {});
	}

	serializeSettings(): GlobalState["timer"] {
		const { advanceRate, autoAdvance, timed } = this.config;
		return { advanceRate, autoAdvance, timed };
	}

	getTimed(): boolean {
		return this.config.timed;
	}
}

export class NullTimer extends Timer<never> {
	viewContext(current: number, context: string): void {
		throw new Error("Method not implemented.");
	}

	pause(current: number): void {
		throw new Error("Method not implemented.");
	}

	unpause(current: number): void {
		throw new Error("Method not implemented.");
	}

	reset(current: number): void {
		throw new Error("Method not implemented.");
	}
}

export interface PqTimerExtras extends TimerExtras {
	contexts_viewed: { [id: string]: boolean };
}

export class PqTimer extends Timer<PqTimerExtras> {
	viewContext(current: number, context: string): void {
		if (current in this.times) {
			const lastTime = this.times[current].at(-1);
			if (lastTime.extras) {
				lastTime.extras.contexts_viewed[context] = true;
			}
		}
	}

	pause(current: number): void {
		if (
			current in this.times
			&& this.times[current].length > 0
			&& this.times[current].at(-1).end === undefined
		) {
			const lastTime = this.times[current].at(-1)!;
			lastTime.end = new Date().getTime();
		}
	}

	unpause(current: number): void {
		if (!(current in this.times)) { this.times[current] = []; }
		const lastTime = this.times[current].at(-1);
		const now = Date.now();
		if (lastTime && lastTime.end === undefined) { lastTime.end = now; }
		this.times[current].push({
			start: now,
			timed: this.config.timed,
			extras: lastTime?.extras
		});
	}

	reset(current: number): void {
		throw new Error("Method not implemented.");
	}
}

export interface FlashTimerExtras extends TimerExtras {
	front: boolean;
}

export class FlashTimer extends Timer<FlashTimerExtras> {
	viewContext(current: number, context: string): void {
		throw new Error("Method not implemented.");
	}

	pause(current: number): void {
		if (!(current in this.times)) { this.times[current] = []; }
		const lastTime = this.times[current].at(-1);
		const now = Date.now();
		const timed = this.config.timed = false;
		if (lastTime && lastTime.end === undefined) { lastTime.end = now; }
		this.times[current].push({
			start: now,
			timed,
			extras: lastTime?.extras
		});
	}

	unpause(current: number): void {
		if (!(current in this.times)) { this.times[current] = []; }
		const lastTime = this.times[current].at(-1);
		const now = Date.now();
		const timed = this.config.timed = true;
		if (lastTime && lastTime.end === undefined) { lastTime.end = now; }
		this.times[current].push({
			start: now,
			timed,
			extras: lastTime?.extras
		});
	}

	reset(current: number): void {
		const lastTime = this.times[current].at(-1);
		this.switch(current, current, lastTime.extras);
		this.lastTick = this.lastPing = new Date().getTime();
		this.initial = {};
		this.times = {[current]: [this.times[current].at(-1)]};
	}
}